import { useEffect, useState, useContext } from 'react';
import Layout from '@/components/Layout';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Gamepad2, ArrowLeft, Trash2 } from 'lucide-react';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_LABELS = {
  'basic': 'BASIC PRACTICE',
  'comps_ready': "COMP'S READY",
  'advanced': 'ADVANCED'
};

export default function MatchingCategoryPage() {
  const { tier, categorySlug } = useParams();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [matchingGames, setMatchingGames] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [tier, categorySlug]);

  const loadData = async () => {
    try {
      // Load all matching games and filter by tier and category
      const [gamesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/matching-games`),
        axios.get(`${API}/test-categories`) // Public endpoint
      ]);
      
      // Find the category
      const foundCategory = categoriesRes.data.find(cat => cat.slug === categorySlug);
      setCategory(foundCategory);
      
      // Filter matching games for this tier and category
      const filtered = gamesRes.data.filter(
        g => g.tier === tier && g.category === categorySlug
      );
      setMatchingGames(filtered);
    } catch (error) {
      console.error('Failed to load matching games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGameClick = (gameId) => {
    navigate(`/matching/${gameId}`);
  };

  const handleBackClick = () => {
    navigate('/matching-center');
  };

  const handleDelete = async (gameId, title) => {
    if (!window.confirm(`Delete Matching game "${title || 'this game'}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/matching-games/${gameId}`, { withCredentials: true });
      setMatchingGames(prev => prev.filter(g => g.id !== gameId));
      toast.success('Matching game deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete game');
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <div 
        className="relative bg-gradient-to-br from-purple-500 via-pink-500 to-purple-600 h-[300px] mb-12 flex items-center justify-center"
      >
        <div className="container mx-auto px-6">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white hover:text-[#F4E4C1] transition-colors mb-6"
          >
            <ArrowLeft className="w-6 h-6" />
            <span className="text-lg font-bold">Back to Matching Center</span>
          </button>
          <div className="text-center">
            <div className="text-6xl mb-4">{category?.icon || '🎮'}</div>
            <h1 className="text-5xl md:text-6xl retro-title text-white mb-2">
              {category?.name.toUpperCase() || categorySlug.toUpperCase()}
            </h1>
            <p className="text-xl text-white font-bold mb-2">
              {TIER_LABELS[tier] || tier.toUpperCase()}
            </p>
            <p className="text-lg text-white opacity-90">
              {matchingGames.length} Matching Game{matchingGames.length !== 1 ? 's' : ''} Available
            </p>
          </div>
        </div>
      </div>

      {/* Matching Games Grid */}
      <div className="container mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="text-2xl text-[#6B4423]">Loading matching games...</div>
          </div>
        ) : matchingGames.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matchingGames.map((game) => (
              <div key={game.id} className="relative" data-testid={`matching-game-card-${game.id}`}>
                {isAdmin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(game.id, game.test_title); }}
                    className="absolute top-3 right-3 z-10 bg-[#E85D4D] text-white p-2 rounded-full hover:bg-[#d14a3b] transition-colors shadow-lg"
                    title="Delete this Matching game"
                    data-testid={`delete-matching-button-${game.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleGameClick(game.id)}
                  className="w-full cassette-card bg-[#F4E4C1] p-8 text-left transition-all transform hover:scale-105 hover:shadow-2xl"
                >
                  <div className="flex items-start justify-between mb-4">
                    <Gamepad2 className="w-12 h-12 text-purple-600" />
                    <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {game.sets && game.sets.length > 0 ? game.sets.flat().length : (game.pairs?.length || 0)} Pairs
                      {game.sets && game.sets.length > 1 ? ` (${game.sets.length} sets)` : ''}
                    </span>
                  </div>
                  <h3 className="text-2xl retro-title text-[#2C1810] mb-3">
                    {game.test_title || 'Matching Game'}
                  </h3>
                  <p className="text-[#6B4423] text-sm">
                    Match terms and definitions
                  </p>
                  <div className="mt-4 pt-4 border-t-2 border-[#6B4423]/20">
                    <p className="text-xs text-[#6B4423] opacity-70">
                      Created: {new Date(game.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Gamepad2 className="w-24 h-24 text-[#6B4423] mx-auto mb-6 opacity-50" />
            <h3 className="text-3xl retro-title text-[#2C1810] mb-4">
              No Matching Games Available
            </h3>
            <p className="text-lg text-[#6B4423]">
              No matching games have been created for this category yet.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
